import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './PlannerAdmin.css';

interface Alert {
  id: number;
  alert_type: string;
  alert_type_display: string;
  customer_name: string;
  message: string;
  status: string;
  status_display: string;
  created_at: string;
}

interface Plan {
  id: number;
  name: string;
  customer_name: string;
  year: number;
  status: string;
  status_display: string;
  batch_count: number;
  active_batch_count: number;
  created_at: string;
}

interface Batch {
  id: string;
  batch_number: string;
  profile_name: string;
  customer_name: string;
  quantity_started: number;
  arrival_date: string;
  status: string;
  status_display: string;
  current_week: number;
}

interface DashboardData {
  total_active_plans: number;
  total_plans: number;
  total_active_batches: number;
  pending_verifications: number;
  overdue_verifications: number;
  orders_shipping_this_week: number;
  active_weather_alerts: number;
  arriving_this_week: number;
  unresolved_alerts: Alert[];
  recent_plans: Plan[];
  recent_batches: Batch[];
}

const PlannerAdmin: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/planner/admin/dashboard/');
      setData(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load planner dashboard:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveAlert = async (alertId: number) => {
    try {
      await api.post(`/planner/admin/alerts/${alertId}/resolve/`);
      fetchDashboard();
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="planner-admin-loading">Loading Farmhand Admin...</div>;
  }

  if (error) {
    return (
      <div className="planner-admin">
        <div className="admin-header">
          <h1>Farmhand - Admin</h1>
        </div>
        <div className="planner-error">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="planner-admin">
      <div className="admin-header">
        <h1>Farmhand - Admin</h1>
        <div className="header-actions">
          <Link to="/planner/egg-watch" className="btn-secondary">Egg Watch</Link>
          <Link to="/planner/plans" className="btn-secondary">Manage Plans</Link>
          <Link to="/planner/batches" className="btn-secondary">All Batches</Link>
          <Link to="/planner/orders" className="btn-secondary">Planned Orders</Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{data.total_active_plans}</div>
          <div className="stat-label">Active Plans</div>
          <div className="stat-sublabel">{data.total_plans} total</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.total_active_batches}</div>
          <div className="stat-label">Active Batches</div>
        </div>
        <div className="stat-card highlight-warning">
          <div className="stat-value">{data.pending_verifications}</div>
          <div className="stat-label">Pending Verifications</div>
          {data.overdue_verifications > 0 && (
            <div className="stat-badge danger">{data.overdue_verifications} overdue</div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.orders_shipping_this_week}</div>
          <div className="stat-label">Shipping This Week</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.arriving_this_week}</div>
          <div className="stat-label">Arriving This Week</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.active_weather_alerts}</div>
          <div className="stat-label">Weather Alerts</div>
        </div>
      </div>

      {/* Alerts Section */}
      {data.unresolved_alerts.length > 0 && (
        <div className="alerts-section">
          <h2>Attention Required</h2>
          <div className="alerts-list">
            {data.unresolved_alerts.map(alert => (
              <div key={alert.id} className={`alert-card ${alert.alert_type}`}>
                <div className="alert-header">
                  <span className="alert-type">{alert.alert_type_display}</span>
                  <span className="alert-status">{alert.status_display}</span>
                </div>
                <div className="alert-body">
                  <span className="alert-customer">{alert.customer_name}</span>
                  <p className="alert-message">{alert.message}</p>
                </div>
                <div className="alert-footer">
                  <span className="alert-date">{formatDate(alert.created_at)}</span>
                  <button
                    className="btn-resolve"
                    onClick={() => handleResolveAlert(alert.id)}
                  >
                    Resolve
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Plans & Batches Grid */}
      <div className="dashboard-grid">
        {/* Recent Plans */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2>Recent Plans</h2>
            <Link to="/planner/plans" className="view-all">View All</Link>
          </div>
          {data.recent_plans.length === 0 ? (
            <p className="empty-message">No plans yet</p>
          ) : (
            <div className="data-table compact">
              <table>
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Customer</th>
                    <th>Year</th>
                    <th>Batches</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_plans.map(plan => (
                    <tr key={plan.id}>
                      <td>
                        <Link to={`/planner/plans/${plan.id}`} className="link-primary">
                          {plan.name}
                        </Link>
                      </td>
                      <td>{plan.customer_name}</td>
                      <td>{plan.year}</td>
                      <td>{plan.active_batch_count}/{plan.batch_count}</td>
                      <td>
                        <span className={`status-badge ${plan.status}`}>
                          {plan.status_display}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Batches */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2>Recent Batches</h2>
            <Link to="/planner/batches" className="view-all">View All</Link>
          </div>
          {data.recent_batches.length === 0 ? (
            <p className="empty-message">No batches yet</p>
          ) : (
            <div className="data-table compact">
              <table>
                <thead>
                  <tr>
                    <th>Batch #</th>
                    <th>Customer</th>
                    <th>Type</th>
                    <th>Qty</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_batches.map(batch => (
                    <tr key={batch.id}>
                      <td>
                        <Link to={`/planner/batches/${batch.id}`} className="link-primary">
                          {batch.batch_number}
                        </Link>
                      </td>
                      <td>{batch.customer_name}</td>
                      <td>{batch.profile_name}</td>
                      <td>{batch.quantity_started}</td>
                      <td>
                        <span className={`status-badge ${batch.status}`}>
                          {batch.status_display}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/planner/plans/new" className="action-card">
            <span className="action-icon">+</span>
            <span className="action-title">Create New Plan</span>
            <span className="action-desc">Set up a seasonal plan for a customer</span>
          </Link>
          <Link to="/planner/batches/new" className="action-card">
            <span className="action-icon">+</span>
            <span className="action-title">Add Batch</span>
            <span className="action-desc">Add a new animal batch to a plan</span>
          </Link>
          <Link to="/planner/profiles" className="action-card">
            <span className="action-icon">F</span>
            <span className="action-title">Feed Profiles</span>
            <span className="action-desc">Manage consumption profiles</span>
          </Link>
          <Link to="/planner/weather-tips" className="action-card">
            <span className="action-icon">W</span>
            <span className="action-title">Weather Tips</span>
            <span className="action-desc">Manage weather advice tips</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PlannerAdmin;
